import React, { createContext, useContext, useState, useEffect } from 'react';
import { BackupDataCombined } from 'types';
import _ from 'lodash';
import { useRootState } from 'store/StateProvider';
import {
    calculateAllResults,
    calculateBackupAcv,
    calculateEffectivePrice,
    calculatePriceByCloud,
} from 'views/ResultsTabs/Summary/BackupTable/helpers';

type PdfDataContextType = {
    graphCompleted: boolean;
    backupData: BackupDataCombined;
    backupACV: { hybrid: number[]; cloudNative: number[] };
    effectivePrice: { hybrid: number[]; cloudNative: number[] };
    tabIndex: number;
    timeRange: number;
    timeRangeInSummaryGraph: number;
    priceInformation: boolean;
    isCustomerInfoVisible: boolean;
    changeGraphCompleted: React.Dispatch<React.SetStateAction<boolean>>;
    changeBackupData: React.Dispatch<React.SetStateAction<BackupDataCombined>>;
    changeBackupACV: React.Dispatch<React.SetStateAction<{ hybrid: number[]; cloudNative: number[] }>>;
    changeTabIndex: React.Dispatch<React.SetStateAction<number>>;
    changeTimeRange: React.Dispatch<React.SetStateAction<number>>;
    changeTimeRangeInSummaryGraph: React.Dispatch<React.SetStateAction<number>>;
    changePriceInformation: React.Dispatch<React.SetStateAction<boolean>>;
    changeIsCustomerInfoVisible: React.Dispatch<React.SetStateAction<boolean>>;
};

export const pdfDataInitialState = {
    graphCompleted: false,
    backupData: null,
    backupACV: null,
    effectivePrice: null,
    tabIndex: 0,
    timeRange: 1,
    priceInformation: true,
    timeRangeInSummaryGraph: 1,
    isCustomerInfoVisible: true,
    changeGraphCompleted: (): void => {},
    changeBackupData: (): void => {},
    changeBackupACV: (): void => {},
    changeTabIndex: (): void => {},
    changeTimeRange: (): void => {},
    changePriceInformation: (): void => {},
    changeTimeRangeInSummaryGraph: (): void => {},
    changeIsCustomerInfoVisible: (): void => {},
};

const PdfDataContext = createContext<PdfDataContextType>(pdfDataInitialState);
const { Provider } = PdfDataContext;

interface Props {
    children: React.ReactNode;
}

const PdfDataProvider = ({ children }: Props): JSX.Element => {
    const [backupData, changeBackupData] = useState<BackupDataCombined>(null);
    const [backupACV, changeBackupACV] = useState<{ hybrid: number[]; cloudNative: number[] }>(null);
    const [graphCompleted, changeGraphCompleted] = useState<boolean>(false);
    const [tabIndex, changeTabIndex] = useState<number>(0);
    const {
        calculatedData,
        assumptions,
        sku,
        sourcesData: {
            tcoYear,
            customerDetails: { balanceCredits, customerType },
            firstYearMonths,
        },
    } = useRootState();

    const [timeRange, changeTimeRange] = useState<number>(tcoYear);
    const [effectivePrice, changeEffectivePrice] = useState<{ hybrid: number[]; cloudNative: number[] }>(null);

    const [timeRangeInSummaryGraph, changeTimeRangeInSummaryGraph] = useState<number>(tcoYear);
    const [priceInformation, changePriceInformation] = useState<boolean>(true);
    const [isCustomerInfoVisible, changeIsCustomerInfoVisible] = useState<boolean>(true);

    useEffect(() => {
        changeGraphCompleted(!!backupACV && !!backupData);
    }, [backupACV, backupData]);

    useEffect(() => {
        if (calculatedData.results) {
            changeBackupData(calculateAllResults(calculatedData, balanceCredits, customerType, firstYearMonths));
        }
    }, [calculatedData, calculatedData.results]);

    useEffect(() => {
        let hybridData;
        let cloudNativeData;
        if (assumptions.hybridPricing && backupData?.hybridWorkloads && sku.discounts && sku.plan) {
            hybridData = _.range(tcoYear).map((index) =>
                calculateBackupAcv(
                    sku.discounts[index],
                    calculatePriceByCloud(
                        assumptions.hybridPricing[sku.plan],
                        sku.cloud,
                        assumptions.hybridPricing.govCloudAddon,
                    ),
                    backupData.hybridWorkloads.creditsTotal[index],
                ),
            );
        }
        if (assumptions.cloudNativePricing && backupData?.cloudNativeWorkloads && sku.discounts && sku.plan) {
            cloudNativeData = _.range(tcoYear).map((index) =>
                calculateBackupAcv(
                    sku.discounts[index],
                    calculatePriceByCloud(
                        assumptions.cloudNativePricing[sku.plan],
                        sku.cloud,
                        assumptions.cloudNativePricing.govCloudAddon,
                    ),
                    backupData.cloudNativeWorkloads.creditsTotal[index],
                ),
            );
        }

        if (cloudNativeData && cloudNativeData) changeBackupACV({ hybrid: hybridData, cloudNative: cloudNativeData });
    }, [
        assumptions.cloudNativePricing,
        assumptions.hybridPricing,
        backupData,
        sku.discounts,
        sku.plan,
        sku.cloud,
        tcoYear,
    ]);

    useEffect(() => {
        let hybridData;
        let cloudNativeData;
        if (backupData?.hybridWorkloads.targetData && backupACV) {
            hybridData = _.range(tcoYear).map((index) =>
                calculateEffectivePrice(backupACV.hybrid[index], backupData.hybridWorkloads.targetData[index]),
            );
        }
        if (backupData?.cloudNativeWorkloads.targetData && backupACV) {
            cloudNativeData = _.range(tcoYear).map((index) =>
                calculateEffectivePrice(
                    backupACV.cloudNative[index],
                    backupData.cloudNativeWorkloads.targetData[index],
                ),
            );
        }
        if (hybridData && cloudNativeData) changeEffectivePrice({ hybrid: hybridData, cloudNative: cloudNativeData });
    }, [backupData, backupACV, tcoYear]);

    return (
        <Provider
            value={{
                graphCompleted,
                backupData,
                backupACV,
                effectivePrice,
                tabIndex,
                timeRange,
                timeRangeInSummaryGraph,
                priceInformation,
                isCustomerInfoVisible,
                changeGraphCompleted,
                changeBackupData,
                changeBackupACV,
                changeTabIndex,
                changeTimeRange,
                changeTimeRangeInSummaryGraph,
                changePriceInformation,
                changeIsCustomerInfoVisible,
            }}
        >
            {children}
        </Provider>
    );
};

const usePdfDataContext = (): PdfDataContextType => useContext(PdfDataContext);
export { PdfDataProvider, usePdfDataContext };
